import { PayloadAction, Update } from '@reduxjs/toolkit'
import axios, { AxiosResponse } from 'axios'
import { call, put, select, takeEvery } from 'redux-saga/effects'
import { closeModal } from 'shared/store/modal'
import { addToasterMessage } from 'shared/store/toaster'
import {
  deleteMeetingError,
  deleteMeetingRequest,
  deleteMeetingSuccess,
  fetchMeetingParticipantsForEditError,
  fetchMeetingParticipantsForEditRequest,
  fetchMeetingParticipantsForEditSuccess,
  fetchMeetingParticipantsSilentRequest,
  fetchMeetingParticipantsSilentSuccess,
  fetchMeetingSilentRequest,
  fetchMeetingSilentSuccess,
  meetingError,
  meetingNoticeEmailSent,
  meetingRequest,
  meetingSuccess,
  patchMeetingError,
  patchMeetingRequest,
  patchMeetingSuccess,
  sendParticipantNoticeToAllRequest,
  sendParticipantsNoticeError,
  sendParticipantsNoticeRequest,
  sendParticipantsNoticeSuccess,
  updateMeetingInvitedSendingStatus,
  updateMeetingResponsibleRequested,
  updateMeetingResponsibleSuccess,
  updateMeetingStatusRequested,
  updateMeetingStatusSuccess,
  lastPersonSigned,
  signingCompleted,
} from '.'
import {
  deleteBoardMeeting,
  getBoardMeeting,
  patchBoardMeeting,
  sendNoticeToAllParticipants,
  sendNoticeToParticipants,
  startMeeting,
  cancelMeeting,
  changeSecretaryOnMeeting,
  getMeetingParticipantsForEdit,
  getMeetingParticipants,
} from '../../api/boardMeeting.api'
import { BoardCaseStatusEnum } from '../../models/BoardCase'
import {
  IBoardDetailMeeting,
  IBoardMeetingParticipant,
  IBoardMeetingParticipantForEdit,
  IBoardMeetingPatchWrapper,
  IMeetingAgendaModel,
  MeetingStatusEnum,
} from '../../models/BoardMeeting'
import { BoardActivityStatusEnum } from '../../models/Comments'
import { fetchCommentsSilentRequest } from '../comments'
import { updateAttributesOnMany as updateAttributesOnManyAgendaCases } from '../manage/agenda'
import { getAllAgendaCases } from '../manage/agenda/selector'
import {
  getBoardMeeting as getCurrentBoardMeeting,
  getInvitedParticipants,
} from './selectors'
import { MeetingNoticeInviteReply } from '../../models/MeetingNoticeInviteReply'

function* fetchMeeting(action: PayloadAction<string>) {
  try {
    const result: AxiosResponse<IBoardDetailMeeting> = yield call(
      getBoardMeeting,
      action.payload
    )

    yield put(meetingSuccess(result.data))
  } catch (e: any) {
    if (!axios.isCancel(e)) {
      const errorMessage = (e?.response?.data?.Message as string)
        ? (e?.response?.data?.Message as string)
        : 'En feil skjedde under henting av møte'
      yield put(meetingError(errorMessage))
    }
  }
}

function* fetchMeetingSilent(action: PayloadAction<string>) {
  try {
    const result: AxiosResponse<IBoardDetailMeeting> = yield call(
      getBoardMeeting,
      action.payload
    )
    yield put(fetchMeetingSilentSuccess(result.data))
    yield put(
      fetchCommentsSilentRequest({
        id: result.data.id,
        type: BoardActivityStatusEnum.Meeting,
      })
    )
  } catch (e: any) {
    if (!axios.isCancel(e)) {
      const errorMessage = (e?.response?.data?.Message as string)
        ? (e?.response?.data?.Message as string)
        : 'En feil skjedde under henting av møte'
      console.error(errorMessage)
    }
  }
}

function* fetchMeetingParticipantsSilent(action: PayloadAction<string>) {
  try {
    const result: AxiosResponse<IBoardMeetingParticipant[]> = yield call(
      getMeetingParticipants,
      action.payload
    )
    yield put(fetchMeetingParticipantsSilentSuccess(result.data))
  } catch (e: any) {
    if (!axios.isCancel(e)) {
      const errorMessage = (e?.response?.data?.Message as string)
        ? (e?.response?.data?.Message as string)
        : 'En feil skjedde under henting av møte'
      console.error(errorMessage)
    }
  }
}

function* patchMeeting(action: PayloadAction<IBoardMeetingPatchWrapper>) {
  try {
    const { payload } = action

    yield call(patchBoardMeeting, payload.id, payload.models)
    const patchedMeeting: AxiosResponse<IBoardDetailMeeting> = yield call(
      getBoardMeeting,
      payload.id
    )

    yield put(meetingSuccess(patchedMeeting.data))
    yield put(patchMeetingSuccess())
  } catch (e: any) {
    const errorMessage = (e?.response?.data?.Message as string)
      ? (e?.response?.data?.Message as string)
      : 'En feil skjedde under oppdatering av møte'
    yield put(patchMeetingError(errorMessage))
  }
}

function* deleteMeeting(action: PayloadAction<string>) {
  try {
    yield call(deleteBoardMeeting, action.payload)
    yield put(deleteMeetingSuccess())
  } catch (e: any) {
    const errorMessage = (e?.response?.data?.Message as string)
      ? (e?.response?.data?.Message as string)
      : 'En feil skjedde sletting av møtet'
    yield put(deleteMeetingError(errorMessage))
  }
}

function* sendParticipationsNotice(action: PayloadAction<string[]>) {
  const { data: currentMeeting }: { data: IBoardDetailMeeting | null } =
    yield select(getCurrentBoardMeeting)

  if (!currentMeeting?.id) throw new Error('No meeting id found')

  try {
    if (!action.payload.length) return

    yield call(sendNoticeToParticipants, currentMeeting.id, action.payload)

    const { isSending }: { isSending: boolean | string[] | null } =
      yield select(getInvitedParticipants)

    const sendingStatusUpdate =
      Array.isArray(isSending) && isSending.length
        ? [...isSending, ...action.payload]
        : action.payload

    // Set the ids for who we are waiting for "has sent notice to email queue"
    yield put(updateMeetingInvitedSendingStatus(sendingStatusUpdate))
    yield put(closeModal())

    yield put(sendParticipantsNoticeSuccess())
  } catch (e) {
    console.error(e)
    yield put(sendParticipantsNoticeError('error'))

    yield put(
      addToasterMessage({
        text: `Innkalling kunne ikke bli sendt`,
        type: 'danger',
      })
    )
  }
}

function* sendParticipationNoticeToAll() {
  try {
    const { data: currentMeeting }: { data: IBoardDetailMeeting | null } =
      yield select(getCurrentBoardMeeting)

    if (!currentMeeting?.id) throw new Error('No meeting id found')

    yield call(sendNoticeToAllParticipants, currentMeeting.id)
    yield put(sendParticipantsNoticeSuccess())
    yield put(updateMeetingInvitedSendingStatus(true))
  } catch (e) {
    console.error(e)
    yield put(sendParticipantsNoticeError('error'))

    yield put(
      addToasterMessage({
        text: `Innkalling kunne ikke bli sendt`,
        type: 'danger',
      })
    )
  }
}

function* emailNoticeSent(
  action: PayloadAction<MeetingNoticeInviteReply | null>
) {
  try {
    if (action.payload) {
      const meetingResponse = action.payload
      yield put(
        fetchMeetingParticipantsSilentRequest(meetingResponse.meetingId)
      )
      if (action.payload?.isSuccess) {
        yield put(
          addToasterMessage({
            text: `Innkalling til møte sendt!`,
            type: 'success',
          })
        )
      } else {
        yield put(
          addToasterMessage({
            text: `Innkalling kunne ikke sendes!`,
            type: 'danger',
          })
        )
      }
    }

    yield put(updateMeetingInvitedSendingStatus(null))
  } catch (e) {
    console.error(e)
  }
}

function* updateMeetingOngoingStatus(action: PayloadAction<MeetingStatusEnum>) {
  try {
    const { data: currentMeeting }: { data: IBoardDetailMeeting | null } =
      yield select(getCurrentBoardMeeting)

    const agenda: IMeetingAgendaModel[] = yield select(getAllAgendaCases)

    if (!currentMeeting?.id) throw new Error('No meeting id found')

    const statusToChangeTo = action.payload

    const isChangeToOngoing = statusToChangeTo === MeetingStatusEnum.Ongoing

    if (isChangeToOngoing) {
      yield call(startMeeting, currentMeeting.id)
      // find which agenda items pessimistic to update from status new to ongoing
      const agendaItemsToChange = agenda
        .filter((item) => item.status === BoardCaseStatusEnum.New)
        .map((item) => {
          return {
            id: item.caseId,
            changes: { status: BoardCaseStatusEnum.Ongoing },
          } as Update<IMeetingAgendaModel, string>
        })

      // update the status of the agenda items
      yield put(updateAttributesOnManyAgendaCases(agendaItemsToChange))
    } else {
      yield call(cancelMeeting, currentMeeting.id)
      yield put(closeModal())
    }

    yield put(fetchMeetingSilentRequest(currentMeeting.id))
    yield put(updateMeetingStatusSuccess(action.payload))
  } catch (e) {
    yield put(
      addToasterMessage({
        text: `Klarte ikke oppdatere møte status`,
        type: 'danger',
      })
    )
  }
}

function* updateMeetingResponsible() {
  try {
    const { data: currentMeeting }: { data: IBoardDetailMeeting | null } =
      yield select(getCurrentBoardMeeting)

    if (!currentMeeting?.id) throw new Error('No meeting id found')

    yield call(changeSecretaryOnMeeting, currentMeeting.id)

    yield put(closeModal())

    yield put(fetchMeetingSilentRequest(currentMeeting.id))
    yield put(updateMeetingResponsibleSuccess())
  } catch (err) {
    yield put(updateMeetingResponsibleSuccess())
  }
}

function* fetchMeetingParticipantsForEdit() {
  try {
    const { data: currentMeeting }: { data: IBoardDetailMeeting | null } =
      yield select(getCurrentBoardMeeting)

    if (!currentMeeting?.id) throw new Error('No meeting id found')
    const { data }: AxiosResponse<IBoardMeetingParticipantForEdit[]> =
      yield call(getMeetingParticipantsForEdit, currentMeeting.id)

    yield put(fetchMeetingParticipantsForEditSuccess(data))
  } catch (e) {
    yield put(fetchMeetingParticipantsForEditError(e as string))
  }
}

function* meetingLastPersonSigned(_: PayloadAction<string>) {
  yield put(
    addToasterMessage({
      text: `Protokollen vil bli tilgjengelig i løpet av kort tid`,
    })
  )
}

function* meetingProtocolIsGenerated({ payload }: PayloadAction<string>) {
  yield put(
    addToasterMessage({
      text: `Protokollen er signert og er nå tilgjengelig`,
      type: 'success',
      duration: 7500,
      url: {
        link: `/styrearbeid/moter/mote/${payload}`,
        title: 'Gå til møtet',
      },
    })
  )
}

function* meetingSaga() {
  yield takeEvery(meetingRequest, fetchMeeting)
  yield takeEvery(fetchMeetingSilentRequest, fetchMeetingSilent)
  yield takeEvery(
    fetchMeetingParticipantsSilentRequest,
    fetchMeetingParticipantsSilent
  )
  yield takeEvery(patchMeetingRequest, patchMeeting)
  yield takeEvery(deleteMeetingRequest, deleteMeeting)
  yield takeEvery(sendParticipantsNoticeRequest, sendParticipationsNotice)
  yield takeEvery(
    sendParticipantNoticeToAllRequest,
    sendParticipationNoticeToAll
  )
  yield takeEvery(meetingNoticeEmailSent, emailNoticeSent)
  yield takeEvery(updateMeetingStatusRequested, updateMeetingOngoingStatus)
  yield takeEvery(updateMeetingResponsibleRequested, updateMeetingResponsible)
  yield takeEvery(
    fetchMeetingParticipantsForEditRequest,
    fetchMeetingParticipantsForEdit
  )
  yield takeEvery(lastPersonSigned, meetingLastPersonSigned)
  yield takeEvery(signingCompleted, meetingProtocolIsGenerated)
}

export default meetingSaga
